import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import type { ChangeExtraFields, ChangeExtraValue, ChangeFormField } from '../../../types/changes';
import { extraFieldVisibleFromNatives, weekdayConstraintHint } from '../changeFormSchema';
import ChangeExtraFieldInput from './ChangeExtraFieldInput';

export interface PostImplFormValues {
  rollback_executed: string;
  had_incidents: string;
  change_executed: string;
  change_executed_successfully: string;
  observation: string;
}

interface Props {
  fields: ChangeFormField[];
  form: PostImplFormValues;
  extras: ChangeExtraFields;
  onFormChange: (patch: Partial<PostImplFormValues>) => void;
  onExtraChange: (fieldId: number, value: ChangeExtraValue) => void;
  readOnly: boolean;
}

const YES_NO_KEYS = new Set([
  'rollback_executed',
  'had_incidents',
  'change_executed',
  'change_executed_successfully',
]);

const colProps = (span: number) => (span === 6 ? { xs: 12 as const, md: 6 as const } : { xs: 12 as const });

export function postImplNativeMap(form: PostImplFormValues): Record<string, string> {
  return {
    rollback_executed: form.rollback_executed,
    had_incidents: form.had_incidents,
    change_executed: form.change_executed,
    change_executed_successfully: form.change_executed_successfully,
    observation: form.observation,
  };
}

const ChangePostImplConfigurableForm: React.FC<Props> = ({
  fields,
  form,
  extras,
  onFormChange,
  onExtraChange,
  readOnly,
}) => {
  const natives = postImplNativeMap(form);

  const renderNative = (field: ChangeFormField) => {
    const key = field.native_key;
    if (!key) return null;
    if (YES_NO_KEYS.has(key)) {
      const value = natives[key] || '';
      return (
        <Form.Select
          aria-label={field.label}
          value={value}
          disabled={readOnly}
          onChange={(e) => onFormChange({ [key]: e.target.value } as Partial<PostImplFormValues>)}
        >
          <option value="">Selecione</option>
          <option value="yes">Sim</option>
          <option value="no">Não</option>
        </Form.Select>
      );
    }
    if (key === 'observation') {
      return (
        <Form.Control
          as="textarea"
          rows={3}
          aria-label={field.label}
          value={form.observation}
          disabled={readOnly}
          onChange={(e) => onFormChange({ observation: e.target.value })}
        />
      );
    }
    return null;
  };

  let lastSection = '';
  return (
    <Row className="g-3">
      {fields.map((field) => {
        if (field.kind === 'native' && !field.is_visible && !field.is_locked) return null;
        if (field.kind === 'extra' && !extraFieldVisibleFromNatives(field, fields, natives, extras)) {
          return null;
        }
        const section = field.section_name || '';
        const showSection = Boolean(section && section !== lastSection);
        if (section) lastSection = section;
        return (
          <React.Fragment key={field.id}>
            {showSection ? (
              <Col xs={12}>
                <h6 className="mb-0">{section}</h6>
              </Col>
            ) : null}
            <Col {...colProps(field.col_span)}>
              <Form.Label>{field.label}</Form.Label>
              {field.kind === 'native' ? (
                renderNative(field)
              ) : (
                <fieldset disabled={readOnly} className="border-0 p-0 m-0">
                  <ChangeExtraFieldInput
                    field={field}
                    value={extras[String(field.id)]}
                    onChange={(value) => onExtraChange(field.id, value)}
                  />
                </fieldset>
              )}
              {field.help_text ? (
                <Form.Text className="text-muted">{field.help_text}</Form.Text>
              ) : null}
              {weekdayConstraintHint(field.weekday_constraint) ? (
                <Form.Text className="text-muted d-block">
                  {weekdayConstraintHint(field.weekday_constraint)}
                </Form.Text>
              ) : null}
            </Col>
          </React.Fragment>
        );
      })}
    </Row>
  );
};

export default ChangePostImplConfigurableForm;
